// Koliko seansa zaposlenog košta firmu, a koliko zaposlenog.
//
// Jedino mjesto koje zna za rabat i sufinansiranje firme. Booking, ručni booking
// terapeuta, price-quote i booking UI svi računaju kroz ove funkcije, tako da se
// iznos koji klijent vidi i iznos koji se skine sa kredita / uđe u fakturu
// slažu na fening.
//
// Rabat ide isključivo iz provizije platforme (`computeSessionMoney` dobija punu
// cijenu kao `listPrice`), pa je ograničen na stopu provizije. Zaokruživanje:
// RABAT se zaokružuje naniže, pa greška zaokruživanja nikad ne umanji naplatu.
// Kad je rabat (skoro) jednak proviziji, osnovica umanjene cijene zbog
// zaokruživanja zna pasti fening ispod terapeutovog dijela od pune cijene; tada
// se cijena podiže za najmanji broj feninga (vidi `companyFundingShares`), pa
// zaposleni/firma plate fening više, a terapeut dobija tačno svoj dio.
import type { CompanyBillingModel } from "@/types/company";
import {
  computeSessionMoney,
  type MoneyConfig,
  type SessionMoney,
  type TherapistTaxProfile,
} from "@/lib/money/session-money";

export interface CompanyFundingSettings {
  billingModel: CompanyBillingModel;
  /** Koliko posto cijene plaća firma, 1..100. */
  cofundingPercent: number;
  /** Rabat firme u %, prije ograničenja na proviziju. */
  discountPercent: number;
}

export interface CompanyFundingShares {
  /** Rabat koji je stvarno primijenjen (ograničen na proviziju). */
  discountPercent: number;
  /** Ukupna cijena nakon rabata, u feningima. */
  totalFen: number;
  companyShareFen: number;
  clientShareFen: number;
}

export interface CompanyFunding extends CompanyFundingShares {
  billingModel: CompanyBillingModel;
  cofundingPercent: number;
  listPrice: number;
  total: number;
  companyShare: number;
  clientShare: number;
  /** Firma plaća cijelu seansu, zaposleni ništa. */
  fullyCovered: boolean;
  money: SessionMoney;
}

/** Rabat ograničen na stopu provizije, da nikad ne ide iz terapeutovog dijela. */
export function effectiveCompanyDiscount(discountPercent: number, feePercent: number): number {
  return Math.max(0, Math.min(discountPercent || 0, feePercent));
}

function clampCofunding(percent: number): number {
  if (!Number.isFinite(percent)) return 100;
  return Math.min(100, Math.max(0, percent));
}

// Terapeutov dio ne zavisi od poreskog profila, pa je profil za provjeru nebitan.
const ANY_TAX: TherapistTaxProfile = { payoutMethod: "work_contract", isVatRegistered: false };

/**
 * Najmanja cijena (u feningima, >= `totalFen`) čija osnovica bez PDV-a pokriva
 * terapeutov dio od pune cijene. `computeSessionMoney` terapeutov dio ograničava
 * na osnovicu, pa bi bez ovoga terapeut izgubio fening kad je rabat jednak
 * proviziji (36.48 KM, provizija i rabat 25 %: 23.38 umjesto 23.39).
 */
function liftToTherapistNet(retailFen: number, totalFen: number, money: MoneyConfig): number {
  if (totalFen >= retailFen) return totalFen;
  const fullNet = computeSessionMoney(retailFen / 100, money, ANY_TAX).therapistNet;
  let fen = totalFen;
  while (fen < retailFen && computeSessionMoney(fen / 100, money, ANY_TAX).platformBase < fullNet) fen++;
  return fen;
}

/**
 * Podjela cijene. Čista funkcija, koristi je i klijent. Sa `money` (stope
 * provizije i PDV-a) daje tačno ono što booking naplati; bez nje (klijentski
 * prikaz bez stopa) može u rijetkom slučaju pokazati fening manje.
 */
export function companyFundingShares(
  retailPriceKm: number,
  discountPercent: number,
  cofundingPercent: number,
  money?: MoneyConfig,
): CompanyFundingShares {
  const retailFen = Math.round(retailPriceKm * 100);
  const discountFen = Math.floor((retailFen * discountPercent) / 100);
  const totalFen = money ? liftToTherapistNet(retailFen, retailFen - discountFen, money) : retailFen - discountFen;
  const companyShareFen = Math.round((totalFen * clampCofunding(cofundingPercent)) / 100);
  return {
    discountPercent,
    totalFen,
    companyShareFen,
    clientShareFen: totalFen - companyShareFen,
  };
}

export function resolveCompanyFunding(input: {
  retailPrice: number;
  company: CompanyFundingSettings;
  moneyConfig: MoneyConfig;
  tax: TherapistTaxProfile;
}): CompanyFunding {
  const { retailPrice, company, moneyConfig, tax } = input;
  const discount = effectiveCompanyDiscount(company.discountPercent, moneyConfig.feePercent);
  const cofundingPercent = clampCofunding(company.cofundingPercent);
  const shares = companyFundingShares(retailPrice, discount, cofundingPercent, moneyConfig);
  const total = shares.totalFen / 100;
  return {
    ...shares,
    billingModel: company.billingModel,
    cofundingPercent,
    listPrice: retailPrice,
    total,
    companyShare: shares.companyShareFen / 100,
    clientShare: shares.clientShareFen / 100,
    fullyCovered: shares.clientShareFen === 0,
    money: computeSessionMoney(total, moneyConfig, tax, { listPrice: retailPrice }),
  };
}

/** Kolone seanse koje opisuju ko je šta platio (snapshot pri bookingu). */
export interface SessionFundingRow {
  priceCharged: number | null;
  paymentMethod: string | null;
  companyFundingModel: string | null;
  companyShare: number | null;
  clientShare: number | null;
}

export interface SessionFundingParts {
  /** null = seansa nije na teret firme. */
  model: CompanyBillingModel | null;
  companyShare: number;
  clientShare: number;
}

/**
 * Dio firme i dio klijenta za seansu. Seanse sa kreditom firme upisane prije
 * sufinansiranja nemaju snapshot kolone: firma je platila cijelu cijenu.
 */
export function sessionFundingParts(row: SessionFundingRow): SessionFundingParts {
  const price = row.priceCharged ?? 0;
  if (row.companyFundingModel === "credit" || row.companyFundingModel === "invoice") {
    return {
      model: row.companyFundingModel,
      companyShare: row.companyShare ?? 0,
      clientShare: row.clientShare ?? 0,
    };
  }
  if (row.paymentMethod === "company_credit") {
    return { model: "credit", companyShare: price, clientShare: 0 };
  }
  return { model: null, companyShare: 0, clientShare: price };
}

export interface CancellationSplit {
  companyFee: number;
  clientFee: number;
  /** Koliko se vraća firmi (kredit) ili ne fakturiše, prije naknade. */
  companyRefund: number;
  clientRefund: number;
}

/** Naknada za kasno otkazivanje dijeli se na firmu i klijenta srazmjerno dijelovima. */
export function splitCancellationFee(parts: SessionFundingParts, feeKm: number): CancellationSplit {
  const companyFen = Math.round(parts.companyShare * 100);
  const clientFen = Math.round(parts.clientShare * 100);
  const totalFen = companyFen + clientFen;
  const feeFen = Math.min(totalFen, Math.max(0, Math.round(feeKm * 100)));
  const companyFeeFen = totalFen > 0 ? Math.round((feeFen * companyFen) / totalFen) : 0;
  const clientFeeFen = feeFen - companyFeeFen;
  return {
    companyFee: companyFeeFen / 100,
    clientFee: clientFeeFen / 100,
    companyRefund: (companyFen - companyFeeFen) / 100,
    clientRefund: (clientFen - clientFeeFen) / 100,
  };
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

export interface VatPart {
  /** Bruto, sa PDV-om. */
  total: number;
  base: number;
  vat: number;
}

/**
 * PDV dijela firme i dijela zaposlenog, tako da se sabiraju TAČNO u PDV
 * cijelog iznosa. Zaposleni dobija PST račun na svoj dio (PDV iz njegovog
 * bruto iznosa), a firmi ostaje razlika do PDV-a seanse; nezavisno
 * zaokruživanje oba dijela bi se u ~13 % slučajeva razišlo za fening
 * (77.81 KM, 50 %: 5.65 + 5.65 = 11.30 naspram 11.31).
 *
 * `totalVat` je snimljeni `therapy_sessions.vatAmount` kad dijelovi pokrivaju
 * cijelu seansu; bez njega (stari redovi, naknada za otkazivanje) računa se iz
 * zbira dijelova. Kad firma plaća sve, njen PDV je jednak `totalVat`.
 */
export function splitFundingVat(
  parts: { companyShare: number; clientShare: number },
  vatRate: number,
  totalVat?: number | null,
): { company: VatPart; client: VatPart } {
  const companyTotal = round2(parts.companyShare);
  const clientTotal = round2(parts.clientShare);
  const total = round2(companyTotal + clientTotal);
  const vatAll = totalVat ?? round2(total - round2(total / (1 + vatRate)));
  const clientVat = clientTotal > 0 ? round2(clientTotal - round2(clientTotal / (1 + vatRate))) : 0;
  // Ograda za degenerisan slučaj (firma plaća par feninga): PDV dijela ne smije
  // biti negativan ni veći od samog dijela.
  const companyVat = Math.min(companyTotal, Math.max(0, round2(vatAll - clientVat)));
  return {
    company: { total: companyTotal, base: round2(companyTotal - companyVat), vat: companyVat },
    client: { total: clientTotal, base: round2(clientTotal - clientVat), vat: clientVat },
  };
}
